import React from "react";
import { PartyPopper, Clock, Wrench, Info, ShieldCheck, CalendarClock } from "lucide-react";
import PageHero from "../components/PageHero";
import { images, openings, events, club, yearsOfTradition, openingTime } from "../mock/mock";
import { upcoming, monthShort } from "../lib/dateUtils";

const typeStyles = {
  Veranstaltung: { icon: PartyPopper, badge: "bg-accent/15 text-accent" },
  Arbeitsdienst: { icon: Wrench, badge: "bg-primary/10 text-primary" },
  Hinweis: { icon: Info, badge: "bg-muted text-muted-foreground" },
};

function EmptyNote({ text }) {
  return (
    <div className="flex items-start gap-3 bg-secondary/60 border border-border rounded-xl px-5 py-4 text-muted-foreground">
      <CalendarClock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
      <span>{text}</span>
    </div>
  );
}

export default function Termine() {
  const openOpenings = upcoming(openings);
  const openEvents = upcoming(events);

  return (
    <div>
      <PageHero
        eyebrow="Kalender 2026"
        title="Termine"
        subtitle="Vereinsheimöffnungen und Veranstaltungen – wir freuen uns auf euren Besuch."
        image={images.meadowLake}
      />

      {/* Erlaubnisschein-Banner */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
          <ShieldCheck className="h-8 w-8 text-accent shrink-0" />
          <div className="flex-1">
            <div className="font-600">{club.permit.title}</div>
            <div className="text-sm text-primary-foreground/80">{club.permit.valid} · {yearsOfTradition} Jahre Tradition</div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          {/* Öffnungen */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                <Clock className="h-6 w-6 text-primary" />
              </div>
              <h2 className="font-serif text-3xl font-600 text-foreground">
                Vereinsheimöffnungen
              </h2>
            </div>
            <p className="text-muted-foreground mb-6 -mt-3">
              Jeweils montags von {openingTime} in der Amtsgasse 2, Fürth/Vach.
            </p>
            {openOpenings.length === 0 ? (
              <EmptyNote text="Die nächsten Öffnungstermine werden in Kürze bekannt gegeben. Schaut bald wieder vorbei!" />
            ) : (
              <ul className="space-y-3">
                {openOpenings.map((o, idx) => (
                  <li
                    key={o.date}
                    className={`flex items-center gap-4 rounded-xl px-4 py-3.5 border transition-all ${
                      idx === 0
                        ? "bg-primary/5 border-primary/40 shadow-sm"
                        : "bg-card border-border hover:border-primary/30 hover:shadow-sm"
                    }`}
                  >
                    <div className="text-center shrink-0 w-14">
                      <div className="font-serif text-2xl font-700 text-primary leading-none">
                        {o.date.split(".")[0]}
                      </div>
                      <div className="text-[11px] uppercase text-muted-foreground mt-0.5">
                        {monthShort(o.date)} {o.date.split(".")[2].slice(2)}
                      </div>
                    </div>
                    <div className="h-9 w-px bg-border" />
                    <div className="flex-1">
                      <div className="font-500 text-foreground">Montag</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" /> {openingTime}
                      </div>
                      {o.note && (
                        <div className="text-sm text-primary font-500">{o.note}</div>
                      )}
                    </div>
                    {idx === 0 && (
                      <span className="text-[11px] font-600 uppercase tracking-wider bg-accent text-accent-foreground px-2.5 py-1 rounded-full">
                        Nächster
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Veranstaltungen */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="h-11 w-11 rounded-xl bg-accent/15 flex items-center justify-center">
                <PartyPopper className="h-6 w-6 text-accent" />
              </div>
              <h2 className="font-serif text-3xl font-600 text-foreground">
                Veranstaltungen & Arbeitsdienste
              </h2>
            </div>
            <p className="text-muted-foreground mb-6 -mt-3">
              Höhepunkte im Vereinsjahr sowie Arbeitsdienste (Gewässer dann gesperrt).
            </p>
            {openEvents.length === 0 ? (
              <EmptyNote text="Die Termine für die neue Saison werden derzeit geplant und in Kürze veröffentlicht." />
            ) : (
              <div className="space-y-3">
                {openEvents.map((e, idx) => {
                  const style = typeStyles[e.type] || typeStyles.Hinweis;
                  const Icon = style.icon;
                  return (
                    <div
                      key={e.title + e.date}
                      className={`flex items-center gap-4 rounded-xl px-5 py-4 border transition-shadow ${
                        idx === 0
                          ? "bg-primary/5 border-primary/40 shadow-sm"
                          : "bg-card border-border hover:shadow-sm"
                      }`}
                    >
                      <Icon className="h-5 w-5 text-primary shrink-0" />
                      <div className="flex-1">
                        <div className="font-600 text-foreground">{e.title}</div>
                        <div className="text-sm text-muted-foreground">{e.date}</div>
                      </div>
                      <span className={`text-[11px] font-600 uppercase tracking-wider px-2.5 py-1 rounded-full ${idx === 0 ? "bg-accent text-accent-foreground" : style.badge}`}>
                        {idx === 0 ? "Nächster" : e.type}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
